import * as THREE from 'three'

const COUNT = 6000

// Soft circular glow sprite — white center fading to transparent edge.
// AdditiveBlending + this texture produces the glowing orb look.
function createParticleTexture() {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    const half = size / 2
    const grad = ctx.createRadialGradient(half, half, 0, half, half, half)
    grad.addColorStop(0.0, 'rgba(255,255,255,1.0)')
    grad.addColorStop(0.2, 'rgba(255,255,255,0.7)')
    grad.addColorStop(0.5, 'rgba(255,255,255,0.2)')
    grad.addColorStop(1.0, 'rgba(255,255,255,0.0)')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, size, size)
    return new THREE.CanvasTexture(canvas)
}

// Sample COUNT random surface points from a Three.js geometry using
// barycentric coordinates — gives uniform coverage across flat faces,
// which makes angular shapes like Icosahedron look faceted, not spherical.
function generateShape(idx, count) {
    let geometry
    switch (idx) {
        case 0:  geometry = new THREE.SphereGeometry(0.85, 48, 24);            break
        case 1:  geometry = new THREE.BoxGeometry(1.3, 1.3, 1.3, 6, 6, 6);    break
        case 2:  geometry = new THREE.TorusGeometry(0.72, 0.26, 32, 100);      break
        case 3:  geometry = new THREE.IcosahedronGeometry(0.9, 0);             break  // detail=0 → 20 flat triangular faces
        default: geometry = new THREE.TorusKnotGeometry(0.55, 0.18, 200, 20);  break
    }

    // Flatten to non-indexed so every 3 positions = one triangle
    const flat = geometry.toNonIndexed()
    geometry.dispose()

    const attr     = flat.attributes.position
    const triCount = Math.floor(attr.count / 3)
    const pos      = new Float32Array(count * 3)

    for (let i = 0; i < count; i++) {
        // Pick a random triangle
        const t  = Math.floor(Math.random() * triCount) * 3

        const ax = attr.getX(t),     ay = attr.getY(t),     az = attr.getZ(t)
        const bx = attr.getX(t + 1), by = attr.getY(t + 1), bz = attr.getZ(t + 1)
        const cx = attr.getX(t + 2), cy = attr.getY(t + 2), cz = attr.getZ(t + 2)

        // Uniform random point inside triangle via barycentric coords
        let u = Math.random(), v = Math.random()
        if (u + v > 1) { u = 1 - u; v = 1 - v }
        const w = 1 - u - v

        pos[i * 3]     = ax * w + bx * u + cx * v
        pos[i * 3 + 1] = ay * w + by * u + cy * v
        pos[i * 3 + 2] = az * w + bz * u + cz * v
    }

    flat.dispose()
    return pos
}

// One-time per-particle random data — stays fixed across shape morphs
function initParticleData(count) {
    const phases  = new Float32Array(count)   // oscillation phase offset
    const speeds  = new Float32Array(count)   // oscillation speed
    const drags   = new Float32Array(count)   // spring damping (varies = staggered return)
    const offDirs = new Float32Array(count * 3) // random unit vector per particle

    for (let i = 0; i < count; i++) {
        phases[i] = Math.random() * Math.PI * 2
        speeds[i] = 0.4 + Math.random() * 1.6
        drags[i]  = 0.82 + Math.random() * 0.10

        // Random unit vector on sphere surface
        const theta  = Math.random() * Math.PI * 2
        const phi    = Math.acos(2 * Math.random() - 1)
        offDirs[i * 3]     = Math.sin(phi) * Math.cos(theta)
        offDirs[i * 3 + 1] = Math.sin(phi) * Math.sin(theta)
        offDirs[i * 3 + 2] = Math.cos(phi)
    }

    return { phases, speeds, drags, offDirs }
}

export default class ParticlesPreset {
    constructor() {
        this.id   = 'particles'
        this.name = 'Particles'
        this.plan = 'free'
        this.defaultObjectColor = '#01edfe'
        this.defaultBgColor     = '#020208'

        this._config = {}

        this._renderer = null
        this._scene    = null
        this._camera   = null
        this._points   = null
        this._geometry = null
        this._material = null

        this._positions     = null
        this._basePositions = null
        this._velocity      = null
        this._fromPositions = null
        this._toPositions   = null
        this._morphT        = 1.0
        this._shapeIdx      = 0

        // Per-particle data (random, constant across morphs)
        this._phases  = null
        this._speeds  = null
        this._drags   = null
        this._offDirs = null
    }

    // ── Config API ────────────────────────────────────────────────────────────

    getSchema(plan) {
        if (plan === 'free') return []
        const fields = [
            { key: 'objectColor', type: 'color', label: 'Particle Color', default: this.defaultObjectColor },
            { key: 'bgColor',     type: 'color', label: 'Background',     default: this.defaultBgColor },
        ]
        if (plan === 'pro') {
            fields.push({
                key: 'shapeIdx', type: 'select', label: 'Shape',
                options: [
                    { value: 0, label: 'Sphere'     },
                    { value: 1, label: 'Cube'        },
                    { value: 2, label: 'Torus'       },
                    { value: 3, label: 'Icosahedron' },
                    { value: 4, label: 'Torus Knot'  },
                ],
                default: 0,
            })
        }
        return fields
    }

    setConfig(cfg) {
        const prevShape = Math.round(Number(this._config.shapeIdx ?? 0))
        this._config    = { ...this._config, ...cfg }
        const newShape  = Math.round(Number(this._config.shapeIdx  ?? 0))

        if (newShape !== prevShape && this._positions) {
            this._startMorph(newShape)
            this._shapeIdx = newShape
        }
        if (this._material)           this._material.color.set(this._config.objectColor ?? this.defaultObjectColor)
        if (this._scene?.background)  this._scene.background.set(this._config.bgColor   ?? this.defaultBgColor)
    }

    getConfig() { return this._config }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    init(canvas) {
        this._renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false })
        this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this._renderer.setSize(canvas.width, canvas.height, false)

        this._scene            = new THREE.Scene()
        this._scene.background = new THREE.Color(this.defaultBgColor)

        this._camera            = new THREE.PerspectiveCamera(60, canvas.width / canvas.height, 0.1, 100)
        this._camera.position.z = 2.5

        this._positions     = generateShape(0, COUNT)
        this._basePositions = new Float32Array(this._positions)
        this._velocity      = new Float32Array(COUNT * 3)
        this._shapeIdx      = 0

        const pd = initParticleData(COUNT)
        this._phases  = pd.phases
        this._speeds  = pd.speeds
        this._drags   = pd.drags
        this._offDirs = pd.offDirs

        this._geometry = new THREE.BufferGeometry()
        this._geometry.setAttribute('position', new THREE.BufferAttribute(this._positions, 3))

        this._texture  = createParticleTexture()
        this._material = new THREE.PointsMaterial({
            size:            0.038,
            map:             this._texture,
            color:           new THREE.Color(this.defaultObjectColor),
            transparent:     true,
            opacity:         0.90,
            sizeAttenuation: true,
            blending:        THREE.AdditiveBlending,
            depthWrite:      false,
            alphaTest:       0.001,
        })

        this._points = new THREE.Points(this._geometry, this._material)
        this._scene.add(this._points)
        this._morphT = 1.0
    }

    tick({ time, audioLevel = 0, bass = 0, mid = 0, treble = 0, hover = 0 }) {
        if (!this._renderer) return

        // Sync canvas size
        const canvas = this._renderer.domElement
        const w = canvas.width, h = canvas.height
        if (Math.abs(this._camera.aspect - w / h) > 0.001) {
            this._camera.aspect = w / h
            this._camera.updateProjectionMatrix()
            this._renderer.setSize(w, h, false)
        }

        const pos     = this._positions
        const vel     = this._velocity
        const base    = this._basePositions
        const phases  = this._phases
        const speeds  = this._speeds
        const drags   = this._drags
        const offDirs = this._offDirs

        // ── Morph (cubic ease-out, ~2 s) ─────────────────────────────────────
        if (this._morphT < 1.0) {
            this._morphT = Math.min(1.0, this._morphT + 0.008)
            const t    = 1 - Math.pow(1 - this._morphT, 3)
            const from = this._fromPositions
            const to   = this._toPositions
            for (let i = 0; i < COUNT * 3; i++) pos[i] = from[i] + (to[i] - from[i]) * t
            if (this._morphT >= 1.0) {
                for (let i = 0; i < COUNT * 3; i++) base[i] = to[i]
            }

        } else {
            // ── Per-particle spring physics + individual oscillation ───────────
            // TorusKnot (idx 4) uses tight parameters to preserve intricate geometry;
            // all other shapes use loose/dramatic settings for the preview look.
            const isKnot  = (this._shapeIdx === 4)
            const push    = bass   * (isKnot ? 0.68 : 1.1)
            const turb    = treble * (isKnot ? 0.05 : 0.10)
            const spring  = isKnot ? 9.0 : 6.5
            const dt      = 0.016
            const waveAmp = isKnot
                ? 0.05 * (0.4 + audioLevel * 1.8)   // knot: barely moves, shape preserved
                : 0.10  * (0.5 + audioLevel * 3.0)   // others: dramatic spread

            for (let i = 0; i < COUNT; i++) {
                const i3 = i * 3
                const bx = base[i3], by = base[i3 + 1], bz = base[i3 + 2]
                const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1

                // Each particle oscillates along its own random direction at its own speed
                const wave = Math.sin(time * speeds[i] + phases[i]) * waveAmp
                const rx = bx + offDirs[i3]     * wave
                const ry = by + offDirs[i3 + 1] * wave
                const rz = bz + offDirs[i3 + 2] * wave

                // Spring toward animated rest + radial bass push + treble turbulence
                vel[i3]     += (-(pos[i3]     - rx) * spring + (bx / len) * push + (Math.random() - 0.5) * turb) * dt
                vel[i3 + 1] += (-(pos[i3 + 1] - ry) * spring + (by / len) * push + (Math.random() - 0.5) * turb) * dt
                vel[i3 + 2] += (-(pos[i3 + 2] - rz) * spring + (bz / len) * push + (Math.random() - 0.5) * turb) * dt

                // Per-particle drag → staggered return after bass explosion
                vel[i3]     *= drags[i]
                vel[i3 + 1] *= drags[i]
                vel[i3 + 2] *= drags[i]

                pos[i3]     += vel[i3]
                pos[i3 + 1] += vel[i3 + 1]
                pos[i3 + 2] += vel[i3 + 2]
            }
        }

        this._geometry.attributes.position.needsUpdate = true

        // ── Rotation — torus spins on Z so the hole always faces camera ───────
        if (this._shapeIdx === 2) {
            this._points.rotation.x = Math.sin(time * 0.06) * 0.18 + mid * 0.10
            this._points.rotation.y = time * 0.1 + hover * 0.35
            this._points.rotation.z = time * 0.12 + hover * 0.35
        } else {
            this._points.rotation.y = time * 0.15 + hover * 0.35
            this._points.rotation.x = Math.sin(time * 0.07) * 0.18 + mid * 0.14
            this._points.rotation.z = Math.cos(time * 0.05) * 0.18 + hover * 0.22
        }

        this._material.size    = 0.034 + audioLevel * 0.018
        this._material.opacity = 0.85  + audioLevel * 0.12

        this._renderer.render(this._scene, this._camera)
    }

    destroy() {
        if (!this._renderer) return
        this._geometry?.dispose()
        this._texture?.dispose()
        this._material?.dispose()
        this._renderer.dispose()
        this._renderer      = null
        this._scene         = null
        this._camera        = null
        this._points        = null
        this._geometry      = null
        this._material      = null
        this._texture       = null
        this._positions     = null
        this._basePositions = null
        this._velocity      = null
        this._fromPositions = null
        this._toPositions   = null
        this._phases        = null
        this._speeds        = null
        this._drags         = null
        this._offDirs       = null
    }

    _startMorph(toShapeIdx) {
        this._fromPositions = new Float32Array(this._positions)
        this._toPositions   = generateShape(toShapeIdx, COUNT)
        this._morphT        = 0.0
    }
}
